import { EventEmitter, useMount } from '@apia/util';
import { ReactNode, useCallback, useState } from 'react';
import {
  ConfirmModal,
  IConfirm,
} from '../../../components/dialogs/ConfirmModal';
import { AlertModal } from '../../../components/dialogs/AlertModal';

type TApiaUtilConfirm = Pick<
  IConfirm,
  | 'additionalButtons'
  | 'additionalButtonsOnRight'
  | 'cancelButtonText'
  | 'children'
  | 'className'
  | 'confirmButtonText'
  | 'confirmButtonVariant'
  | 'contentRef'
  | 'hideCancelButton'
  | 'hideConfirmButton'
  | 'title'
  | 'variant'
>;

type TApiaUtilInnerConfirm = TApiaUtilConfirm & { id: string } & Pick<
    IConfirm,
    'onCancel' | 'onConfirm'
  >;

type TAlert = {
  children: ReactNode;
  title: string;
};

type TApiaUtilInnerAlert = TAlert & { onClose: () => unknown; id: string };

export class ApiaUtilDialogs {
  #emitter = new EventEmitter<{
    alert: TApiaUtilInnerAlert;
    confirm: TApiaUtilInnerConfirm;
  }>();
  #maxIdAlert = 0;
  #maxIdConfirm = 0;

  alert(props: TAlert): Promise<void> {
    return new Promise((resolve) => {
      this.#emitter.emit('alert', {
        ...props,
        onClose: () => resolve(),
        id: `confirm${this.#maxIdAlert++}`,
      });
    });
  }

  confirm(props: TApiaUtilConfirm): Promise<boolean> {
    return new Promise((resolve) => {
      this.#emitter.emit('confirm', {
        ...props,
        id: `confirm${this.#maxIdConfirm++}`,
        onCancel: () => resolve(false),
        onConfirm: () => resolve(true),
      });
    });
  }

  Component = () => {
    const [confirmDialogs, setConfirmDialogs] = useState<
      TApiaUtilInnerConfirm[]
    >([]);
    const [alertsDialogs, setAlertsDialogs] = useState<TApiaUtilInnerAlert[]>(
      [],
    );

    useMount(() => {
      const unsuscribe1 = this.#emitter.on('alert', (ev) => {
        setAlertsDialogs((current) => [...current, ev]);
      });
      const unsuscribe2 = this.#emitter.on('confirm', (ev) => {
        setConfirmDialogs((current) => [...current, ev]);
      });

      return () => {
        unsuscribe1();
        unsuscribe2();
      };
    });

    const removeModal = useCallback(
      (which: TApiaUtilConfirm & { id: string }) => {
        setConfirmDialogs((current) =>
          current.filter((search) => search !== which),
        );
      },
      [],
    );

    return (
      <>
        {alertsDialogs.map((current) => (
          <AlertModal {...current} key={current.id} />
        ))}
        {confirmDialogs.map((current) => (
          <ConfirmModal
            {...current}
            key={current.id}
            onCancel={() => {
              removeModal(current);
              current.onCancel?.();
            }}
            onConfirm={() => {
              removeModal(current);
              current.onConfirm?.();
            }}
          />
        ))}
      </>
    );
  };
}
